import { Form, Input, Modal, Statistic } from 'antd';
import auth from 'api/auth';
import axios, { CancelTokenSource } from 'axios';
import { TFunction } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import classes from './TFAuthModal.module.scss';
import { saveTokensAfterAuthentication } from 'helpers/cookies';
import { useNavigate } from 'react-router';
import { getTheme } from 'helpers/getTheme';
import { useAppSelector } from 'store/hook';
import { functionCollectBrowserData } from 'helpers/browserData/functionCollectBrowserData';

type Props = {
  isOpen: boolean;
  email: string;
  onCancel: () => void;
  t: TFunction;
};

const expiredMinutes = 10;

export default function TFAuthModal({ email, isOpen, onCancel, t }: Props) {
  const [otp, setOtp] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [deadLine, setDeadLine] = useState<number>(0);

  const { messageApi } = useAppSelector(store => store.windowState);
  
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setDeadLine(Date.now() + expiredMinutes * 60 * 1000);
  }, []);

  const onSubmit = async (auth_password: string) => {
    try {
      setIsLoading(true);
      const browserData = await functionCollectBrowserData();
      
      cancelTokenRef.current = axios.CancelToken.source();
      const formData = new FormData();
      formData.append('auth_password', auth_password);
      formData.append('email', email);
      formData.append('browser_data', JSON.stringify(browserData) || '');
      const response = await auth.verifyAuth(
        { auth_password, email, browser_data: JSON.stringify(browserData) || '' },
        formData,
        cancelTokenRef.current.token
      );

      saveTokensAfterAuthentication(response.data);

      messageApi?.destroy();
      navigate('/');
      onCancel();
    } catch (err: any) {
      if (err?.message === 'Aborted' || err?.name === 'CanceledError') return;
      messageApi?.error(t('login.modals.verify.wrong'));
    } finally {
      setIsLoading(false);
    }
  };

  const onChange = (value: string) => {
    cancelTokenRef.current?.cancel();
    if (value.length === 6) {
      onSubmit(value);
    }
    setOtp(value);
  };

  return (
    <Modal
      onCancel={onCancel}
      open={isOpen}
      title={t('login.modals.verify.title')}
      okText={t('main.confirmButton')}
      cancelText={t('main.cancelButton')}
      className={getTheme()}
      classNames={{
        body: classes.body,
      }}
      okButtonProps={{
        disabled: otp.length !== 6,
        loading: isLoading,
      }}
    >
      <Form>
        <Statistic.Countdown
          className={classes.countDown}
          onFinish={onCancel}
          title={t('login.modals.verify.expired')}
          value={deadLine}
          format='mm:ss'
        />
        <Form.Item>
          <Input.OTP
            style={{
              justifyContent: 'space-between',
              width: '80%',
              margin: '20px auto 0',
              display: 'flex',
            }}
            value={otp}
            onChange={onChange}
            autoFocus
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
